<template>
  <div class="tutorial-connection">
    <div class="space-y-6">
      <!-- Example connection monitor: illustrative values, not measurements -->
      <div class="bg-white rounded-lg border border-gray-200 p-6">
        <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IconifyIcon icon="heroicons:signal-20-solid" class="w-5 h-5 text-green-600" />
          Connection Monitor
          <span class="text-xs font-normal text-gray-500">(example values, not measured)</span>
        </h4>

        <!-- Connection Status Grid -->
        <div class="grid md:grid-cols-2 gap-6">
          <!-- Connection State -->
          <div class="space-y-4">
            <h5 class="font-medium text-gray-900">Connection State</h5>
            <div class="space-y-3">
              <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span class="text-sm text-gray-600">ICE State</span>
                <span class="text-sm font-medium text-green-600">{{ iceState }}</span>
              </div>
              <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span class="text-sm text-gray-600">Signaling State</span>
                <span class="text-sm font-medium text-blue-600">{{ signalingState }}</span>
              </div>
              <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span class="text-sm text-gray-600">Data Channel State</span>
                <span class="text-sm font-medium text-purple-600">{{ dataChannelState }}</span>
              </div>
            </div>
          </div>

          <!-- Performance Metrics -->
          <div class="space-y-4">
            <h5 class="font-medium text-gray-900">Performance Metrics</h5>
            <div class="space-y-3">
              <div class="p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm text-gray-600">Bandwidth</span>
                  <span class="text-sm font-medium">{{ bandwidth }} Mbps</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="bg-green-500 h-2 rounded-full transition-all duration-500" :style="{ width: `${(bandwidth / 10) * 100}%` }"></div>
                </div>
              </div>
              <div class="p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm text-gray-600">Latency</span>
                  <span class="text-sm font-medium">{{ latency }} ms</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="bg-blue-500 h-2 rounded-full transition-all duration-500" :style="{ width: `${(latency / 200) * 100}%` }"></div>
                </div>
              </div>
              <div class="p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm text-gray-600">Packet Loss</span>
                  <span class="text-sm font-medium">{{ packetLoss }}%</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="bg-yellow-500 h-2 rounded-full transition-all duration-500" :style="{ width: `${packetLoss}%` }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Connection Flow Visualization -->
      <div class="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6">
        <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IconifyIcon icon="heroicons:arrow-path-20-solid" class="w-5 h-5 text-blue-600" />
          P2P Connection Flow
        </h4>

        <div class="relative">
          <!-- Connection Diagram -->
          <div class="flex items-center justify-between">
            <!-- Host Device -->
            <div class="text-center">
              <div class="w-24 h-24 bg-blue-100 rounded-lg flex items-center justify-center mb-2">
                <IconifyIcon icon="heroicons:server-20-solid" class="w-12 h-12 text-blue-600" />
              </div>
              <p class="text-sm font-medium text-gray-900">Host</p>
              <p class="text-xs text-gray-600">You</p>
            </div>

            <!-- Connection Animation -->
            <div class="flex-1 mx-8">
              <div class="relative">
                <!-- Connection Line -->
                <div class="h-1 bg-gradient-to-r from-blue-400 to-green-400 rounded-full">
                  <div class="h-full bg-white rounded-full animate-pulse" style="width: 30%; margin-left: 35%;"></div>
                </div>
                <!-- Data Particles -->
                <div class="absolute -top-2 left-0 right-0 flex justify-between">
                  <div class="w-3 h-3 bg-blue-500 rounded-full animate-bounce"></div>
                  <div class="w-3 h-3 bg-green-500 rounded-full animate-bounce" style="animation-delay: 0.5s;"></div>
                  <div class="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style="animation-delay: 1s;"></div>
                </div>
                <!-- Connection Type Badge -->
                <div class="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-white px-3 py-1 rounded-full shadow-md border text-xs font-medium text-gray-700">
                  {{ connectionType }}
                </div>
              </div>
            </div>

            <!-- Client Device -->
            <div class="text-center">
              <div class="w-24 h-24 bg-green-100 rounded-lg flex items-center justify-center mb-2">
                <IconifyIcon icon="heroicons:device-phone-mobile-20-solid" class="w-12 h-12 text-green-600" />
              </div>
              <p class="text-sm font-medium text-gray-900">Client</p>
              <p class="text-xs text-gray-600">Peer</p>
            </div>
          </div>

          <!-- Connection Info -->
          <div class="mt-8 grid md:grid-cols-3 gap-4">
            <div class="bg-white rounded-lg p-4 text-center">
              <IconifyIcon icon="heroicons:lock-closed-20-solid" class="w-8 h-8 text-indigo-600 mx-auto mb-2" />
              <h5 class="font-medium text-gray-900">Encrypted</h5>
              <p class="text-sm text-gray-600">DTLS/SRTP encryption</p>
            </div>
            <div class="bg-white rounded-lg p-4 text-center">
              <IconifyIcon icon="heroicons:bolt-20-solid" class="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <h5 class="font-medium text-gray-900">Direct</h5>
              <p class="text-sm text-gray-600">No server intermediary</p>
            </div>
            <div class="bg-white rounded-lg p-4 text-center">
              <IconifyIcon icon="heroicons:arrows-right-left-20-solid" class="w-8 h-8 text-green-600 mx-auto mb-2" />
              <h5 class="font-medium text-gray-900">Bidirectional</h5>
              <p class="text-sm text-gray-600">Full-duplex communication</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Technical Details -->
      <div class="bg-white rounded-lg border border-gray-200 p-6">
        <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IconifyIcon icon="heroicons:cog-6-tooth-20-solid" class="w-5 h-5 text-gray-600" />
          Technical Details
        </h4>

        <div class="grid md:grid-cols-2 gap-6">
          <!-- ICE Candidates -->
          <div>
            <h5 class="font-medium text-gray-900 mb-3">ICE Candidates</h5>
            <div class="space-y-2">
              <div
                v-for="candidate in iceCandidates"
                :key="candidate.id"
                class="p-3 bg-gray-50 rounded-lg border-l-4"
                :class="{
                  'border-green-500': candidate.type === 'host',
                  'border-blue-500': candidate.type === 'srflx',
                  'border-purple-500': candidate.type === 'relay'
                }"
              >
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-900">{{ candidate.ip }}:{{ candidate.port }}</span>
                  <span
                    class="text-xs px-2 py-1 rounded-full"
                    :class="{
                      'bg-green-100 text-green-700': candidate.type === 'host',
                      'bg-blue-100 text-blue-700': candidate.type === 'srflx',
                      'bg-purple-100 text-purple-700': candidate.type === 'relay'
                    }"
                  >
                    {{ candidate.type.toUpperCase() }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Media Streams -->
          <div>
            <h5 class="font-medium text-gray-900 mb-3">Media Streams</h5>
            <div class="space-y-3">
              <div class="p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center gap-2">
                  <IconifyIcon icon="heroicons:video-camera-20-solid" class="w-5 h-5 text-blue-600" />
                  <div>
                    <p class="text-sm font-medium text-gray-900">Video</p>
                    <p class="text-xs text-gray-600">{{ videoCodec }} • {{ videoResolution }}</p>
                  </div>
                </div>
              </div>
              <div class="p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center gap-2">
                  <IconifyIcon icon="heroicons:speaker-wave-20-solid" class="w-5 h-5 text-green-600" />
                  <div>
                    <p class="text-sm font-medium text-gray-900">Audio</p>
                    <p class="text-xs text-gray-600">{{ audioCodec }} • {{ audioChannels }} channels</p>
                  </div>
                </div>
              </div>
              <div class="p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center gap-2">
                  <IconifyIcon icon="heroicons:chat-bubble-left-right-20-solid" class="w-5 h-5 text-purple-600" />
                  <div>
                    <p class="text-sm font-medium text-gray-900">Data Channel</p>
                    <p class="text-xs text-gray-600">{{ dataChannelType }} • {{ dataChannelProtocol }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Troubleshooting -->
      <div class="bg-yellow-50 rounded-lg p-4">
        <div class="flex items-start gap-3">
          <IconifyIcon icon="heroicons:exclamation-triangle-20-solid" class="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h5 class="font-medium text-yellow-900 mb-2">Connection Issues?</h5>
            <ul class="text-sm text-yellow-800 space-y-1">
              <li>• Check firewall settings and network permissions</li>
              <li>• Try using different networks (WiFi vs cellular)</li>
              <li>• Ensure both devices have camera/microphone permissions</li>
              <li>• Some corporate networks may block P2P connections</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import IconifyIcon from '../IconifyIcon.vue'

// Reactive state
const iceState = ref('connected')
const signalingState = ref('stable')
const dataChannelState = ref('open')
const bandwidth = ref(2.5)
const latency = ref(45)
const packetLoss = ref(0.2)
const connectionType = ref('Peer-to-Peer')
const videoCodec = ref('VP8')
const videoResolution = ref('1280x720')
const audioCodec = ref('Opus')
const audioChannels = ref(2)
const dataChannelType = ref('Reliable')
const dataChannelProtocol = ref('SCTP')

const iceCandidates = ref([
  { id: 1, ip: '192.168.1.100', port: 54400, type: 'host' },
  { id: 2, ip: '203.0.113.100', port: 54401, type: 'srflx' },
  { id: 3, ip: 'relay.example.com', port: 443, type: 'relay' }
])

let animationTimer: number | null = null

// Methods
const animateMetrics = () => {
  animationTimer = window.setInterval(() => {
    // Simulate realistic metric variations
    bandwidth.value = Math.max(0.5, Math.min(10, bandwidth.value + (Math.random() - 0.5) * 0.5))
    latency.value = Math.max(10, Math.min(200, latency.value + (Math.random() - 0.5) * 10))
    packetLoss.value = Math.max(0, Math.min(5, packetLoss.value + (Math.random() - 0.5) * 0.1))
  }, 2000)
}

// Lifecycle
onMounted(() => {
  animateMetrics()
})

onUnmounted(() => {
  if (animationTimer) {
    clearInterval(animationTimer)
  }
})
</script>

<style scoped>
.tutorial-connection {
  /* Component-specific styles */
}
</style>